/* eslint-disable no-console */
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import BlockIcon from '@mui/icons-material/Block';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import GroupsIcon from '@mui/icons-material/Groups';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaidIcon from '@mui/icons-material/Paid';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import RestoreIcon from '@mui/icons-material/Restore';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

import {
  Box,
  Container,
  Dialog,
  DialogActions,
  DialogTitle,
  Fab,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Tooltip,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import {
  GridActionsCellItem,
  GridActionsCellItemProps,
  GridColDef,
  GridFilterItem,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid-premium';

import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { isDispatchApiSuccess } from '../../../utils/api';
import { fetchStatuses } from '../../../constants/fetchStatuses';
import { GridActionsCellItemLink } from '../../../components/FakeLink';
import { GridCellExpand } from '../../../components/Table';
import { Table, TableRef } from '../../../components/Table/Table';
import { usePermissions } from '../../../hooks';
import { useAppDispatch } from '../../../redux/hooks';
import {
  approveCompany,
  restoreCompany,
  deleteCompany,
  fetchCompanies,
  fetchCompanyStatuses,
  updateCompany,
  resendActivationLink,
} from '../../../redux/modules/b2bThriftPlan/companies/company.actions';
import {
  companiesLoadingSelector,
  companySelector,
  companyRestoreLoadingSelector,
  resendActivationLinkLoadingSelector,
} from '../../../redux/modules/b2bThriftPlan/companies/company.selectors';
import { locations } from '../../../routes/locations';
import { round2Format, round4Format } from '../../../utils/number';
import * as permissions from '../../../utils/permissions';

const mapDetailsPropertyToFormatFn: Record<string, (v: number) => string> = {
  nav: round2Format,
  numOfUnits: round4Format,
  costBase: round2Format,
  leftUnits: round4Format,
  totalCostBase: round2Format,
  profit: round2Format,
  reconcile: round2Format,
};
export const ThriftCompanies = () => {
  const dispatch = useAppDispatch();
  const { companies, totalCompanies, deleteCompanyStatus, approveCompanyStatus, updateCompanyStatus, companyStatuses } =
    useSelector(companySelector);
  const isLoading = useSelector(companiesLoadingSelector);
  const isRestoreLoading = useSelector(companyRestoreLoadingSelector);
  const isResendActivationLoading = useSelector(resendActivationLinkLoadingSelector);
  const navigate = useNavigate();
  const tableRef = useRef<TableRef>(null);
  const [companyStatusId, setCompanyStatusId] = useState<number | null>(null);
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    type: 'delete' | 'approve' | 'suspend' | 'inactive' | 'restore' | null;
    companyId: number | null;
    company?: any;
  }>({
    open: false,
    type: null,
    companyId: null,
  });
  const [resendActivationCompanyId, setResendActivationCompanyId] = useState<number | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  // Permission checks
  const canCreateCompany = usePermissions({ items: [permissions.Company.create] });
  const canReadCompany = usePermissions({ items: [permissions.Company.read] });
  const canUpdateCompany = usePermissions({ items: [permissions.Company.update] });
  const canDeleteCompany = usePermissions({ items: [permissions.Company.delete] });
  const canReadCompanyDocuments = usePermissions({ items: [permissions.Company.readDocuments] });
  const canCreateCompanyDocument = usePermissions({ items: [permissions.Company.createDocument] });
  const canReadTreasury = usePermissions({ items: [permissions.Treasury.read] });

  useEffect(() => {
    dispatch(fetchCompanyStatuses());
  }, [dispatch]);

  const onAddNew = (): void => {
    navigate(locations.b2bThriftPlan('create'));
  };
  const handleActionDialog = (
    type: 'delete' | 'approve' | 'suspend' | 'inactive' | 'restore',
    companyId: number,
    company?: any,
  ) => {
    setActionDialog({ open: true, type, companyId, company });
  };

  const closeActionDialog = () => {
    setActionDialog({ open: false, type: null, companyId: null });
  };

  const handleResendActivationClick = async (companyId: number) => {
    try {
      setResendActivationCompanyId(companyId);
      const response = await dispatch(resendActivationLink({ companyId })).unwrap();
      const successMessage = response?.data?.message || 'Activation link resent successfully!';
      enqueueSnackbar(successMessage, { variant: 'success' });
    } catch (error: any) {
      const errorMessage =
        error?.message || error?.payload?.message || 'Failed to resend activation link. Please try again.';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setResendActivationCompanyId(null);
    }
  };

  const confirmAction = async () => {
    if (actionDialog.companyId === null || actionDialog.type === null) return;

    try {
      switch (actionDialog.type) {
        case 'delete': {
          const res = await dispatch(deleteCompany(Number(actionDialog.companyId)));
          if (isDispatchApiSuccess(res)) {
            enqueueSnackbar('Company deleted!', { variant: 'success' });
            tableRef.current?.reload();
          } else {
            const errorData = (res as any)?.payload || (res as any)?.error || res;
            const errorMessage = errorData?.message || 'Company delete error!';
            enqueueSnackbar(errorMessage, { variant: 'error' });
          }
          break;
        }
        case 'approve': {
          await dispatch(approveCompany(actionDialog.companyId)).unwrap();
          enqueueSnackbar('Company approved successfully!', { variant: 'success' });
          tableRef.current?.reload();
          break;
        }
        case 'suspend': {
          const suspendedStatus = companyStatuses.find((s) => s.status.toLowerCase() === 'suspended');
          if (!suspendedStatus) {
            enqueueSnackbar('Suspended status not found!', { variant: 'error' });
            return;
          }
          await dispatch(updateCompany({ id: actionDialog.companyId, statusId: suspendedStatus.id })).unwrap();
          enqueueSnackbar('Company suspended successfully!', { variant: 'success' });
          tableRef.current?.reload();
          break;
        }
        case 'inactive': {
          const inactiveStatus = companyStatuses.find((s) => s.status.toLowerCase() === 'inactive');
          if (!inactiveStatus) {
            enqueueSnackbar('Inactive status not found!', { variant: 'error' });
            return;
          }
          await dispatch(updateCompany({ id: actionDialog.companyId, statusId: inactiveStatus.id })).unwrap();
          enqueueSnackbar('Company set to inactive successfully!', { variant: 'success' });
          tableRef.current?.reload();
          break;
        }
        case 'restore': {
          await dispatch(restoreCompany(actionDialog.companyId)).unwrap();
          enqueueSnackbar('Company restored successfully!', { variant: 'success' });
          tableRef.current?.reload();
          break;
        }
        default:
          break;
      }
      closeActionDialog();
    } catch (error: any) {
      const errorMessage = error?.message || `Failed to ${actionDialog.type} company!`;
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const getActionIcon = (type: string | null) => {
    if (type === 'delete') return <DeleteIcon />;
    if (type === 'approve') return <CheckIcon />;
    if (type === 'suspend') return <BlockIcon />;
    if (type === 'inactive') return <CancelIcon />;
    if (type === 'restore') return <RestoreIcon />;
    return undefined;
  };

  const getActionLoading = (type: string | null): boolean => {
    if (type === 'delete') return deleteCompanyStatus === fetchStatuses.pending;
    if (type === 'approve') return approveCompanyStatus === fetchStatuses.pending;
    if (type === 'suspend' || type === 'inactive') return updateCompanyStatus === fetchStatuses.pending;
    if (type === 'restore') return isRestoreLoading;
    return false;
  };

  const getIconColor = (disabled: boolean) => (disabled ? 'inherit' : 'primary');

  const externalFilters: GridFilterItem[] =
    companyStatusId !== null
      ? [
          {
            field: 'statusId',
            value: companyStatusId,
            operator: 'equals',
            id: 'company-status-filter',
          },
        ]
      : [];

  const columns: GridColDef[] = [
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      hideable: false,
      filterable: false,
      sortable: false,
      minWidth: 300,

      getActions: (params: GridRowParams) => {
        const actions: React.ReactElement<GridActionsCellItemProps>[] = [];
        const { isDeleted } = params.row;

        const isCompanyDeleted = Boolean(isDeleted);

        // View/Open action - requires read permission - moved to menu
        if (canReadCompany) {
          actions.push(
            <GridActionsCellItemLink
              key={`b2b-thift-plan-${params.row.id}`}
              icon={<EditOutlinedIcon color={getIconColor(isCompanyDeleted)} />}
              label="Edit"
              href={locations.b2bThriftPlan(params.row.id)}
              showInMenu
              disabled={isCompanyDeleted}
            />,
          );
        }

        // Upload document action - requires read and create document permissions
        if (canReadCompanyDocuments && canCreateCompanyDocument) {
          actions.push(
            <GridActionsCellItemLink
              key={`upload-company-document-${params.row.id}`}
              icon={
                <Tooltip title="Upload Document">
                  <UploadFileIcon color={getIconColor(isCompanyDeleted)} />
                </Tooltip>
              }
              label="Upload Document"
              href={locations.uploadCompanyDocument(params.row.id)}
              disabled={isCompanyDeleted}
            />,
          );
        }

        // Get current company status
        const currentStatus = (params.row.companyStatus?.status || '').trim().toLowerCase();
        const isActive = currentStatus === 'active';
        const isApproved = currentStatus === 'approved' || currentStatus === 'active';

        // View Company Groups action - only enabled if company is active
        if (canReadCompany) {
          actions.push(
            <GridActionsCellItemLink
              key={`view-company-groups-${params.row.id}`}
              icon={
                <Tooltip title="View Company Groups">
                  <GroupsIcon color={getIconColor(!isActive || isCompanyDeleted)} />
                </Tooltip>
              }
              label="View Company Groups"
              href={`/companies/${params.row.id}/groups`}
              disabled={!isActive || isCompanyDeleted}
            />,
          );
        }

        // View Company Departments action - only enabled if company is active
        if (canReadCompany) {
          actions.push(
            <GridActionsCellItemLink
              key={`view-company-departments-${params.row.id}`}
              icon={
                <Tooltip title="View Departments">
                  <BusinessCenterIcon color={getIconColor(!isActive || isCompanyDeleted)} />
                </Tooltip>
              }
              label="View Departments"
              href={`/companies/${params.row.id}/departments`}
              disabled={!isActive || isCompanyDeleted}
            />,
          );
        }
        const isSuspended = currentStatus === 'suspended';
        const isInactive = currentStatus === 'inactive';
        // View Company Employees action - only enabled if company is active
        if (canReadCompany) {
          actions.push(
            <GridActionsCellItemLink
              key={`view-company-employees-${params.row.id}`}
              icon={
                <Tooltip title="View Employees">
                  <SupportAgentIcon color={getIconColor(!isActive || isCompanyDeleted)} />
                </Tooltip>
              }
              label="View Employees"
              href={`/companies/${params.row.id}/employees`}
              disabled={!isActive || isCompanyDeleted}
            />,
          );
        }
        if (canReadCompany) {
          actions.push(
            <GridActionsCellItemLink
              key={`view-company-withdraw-requests-${params.row.id}`}
              icon={
                <Tooltip title="View Withdraw Requests">
                  <PaidIcon color={getIconColor(!isActive || isCompanyDeleted)} />
                </Tooltip>
              }
              label="Company Withdraw Requests"
              href={locations.companyWithdrawRequestList().replace(':companyId', String(params.row.id))}
              disabled={!isActive || isCompanyDeleted}
            />,
          );
        }
        // Treasury action - only enabled if company is active
        if (canReadTreasury) {
          actions.push(
            <GridActionsCellItemLink
              key={`view-company-treasury-${params.row.id}`}
              icon={
                <Tooltip title="Treasury">
                  <AccountBalanceIcon color={getIconColor(!isActive || isCompanyDeleted)} />
                </Tooltip>
              }
              label="Treasury"
              href={locations.companyTreasury(params.row.id)}
              disabled={!isActive || isCompanyDeleted}
            />,
          );
        }
        if (canUpdateCompany && !isActive) {
          actions.push(
            <GridActionsCellItem
              key={`approve-company-${params.row.id}`}
              icon={<ThumbUpIcon color={getIconColor(isCompanyDeleted)} />}
              label="Approve"
              onClick={() => handleActionDialog('approve', params.row.id)}
              showInMenu
              disabled={isCompanyDeleted}
            />,
          );
        }

        // Suspend action (in menu) - requires update permission and only show if not suspended
        if (canUpdateCompany && !isSuspended) {
          actions.push(
            <GridActionsCellItem
              key={`suspend-company-${params.row.id}`}
              icon={<BlockIcon color={getIconColor(isCompanyDeleted)} />}
              label="Suspend"
              onClick={() => handleActionDialog('suspend', params.row.id)}
              showInMenu
              disabled={isCompanyDeleted}
            />,
          );
        }

        // Inactive action (in menu) - requires update permission and only show if not inactive
        if (canUpdateCompany && !isInactive) {
          actions.push(
            <GridActionsCellItem
              key={`inactive-company-${params.row.id}`}
              icon={<CancelIcon color={getIconColor(isCompanyDeleted)} />}
              label="Set Inactive"
              onClick={() => handleActionDialog('inactive', params.row.id)}
              showInMenu
              disabled={isCompanyDeleted}
            />,
          );
        }

        // Delete action (in menu) - requires delete permission - disabled if deleted
        if (canDeleteCompany) {
          actions.push(
            <GridActionsCellItem
              key={`delete-company-${params.row.id}`}
              icon={<DeleteIcon color={getIconColor(isDeleted || deleteCompanyStatus === fetchStatuses.pending)} />}
              label="Delete"
              onClick={() => handleActionDialog('delete', params.row.id, params.row)}
              disabled={isDeleted || deleteCompanyStatus === fetchStatuses.pending}
              showInMenu
            />,
          );
        }

        if (canUpdateCompany) {
          actions.push(
            <GridActionsCellItem
              key={`restore-company-${params.row.id}`}
              icon={<RestoreIcon color={getIconColor(isRestoreLoading && actionDialog.companyId === params.row.id)} />}
              label="Restore"
              onClick={() => handleActionDialog('restore', params.row.id, params.row)}
              disabled={isRestoreLoading && actionDialog.companyId === params.row.id ? true : !isDeleted}
              showInMenu
            />,
          );
        }

        if (canUpdateCompany) {
          actions.push(
            <GridActionsCellItem
              key={`resend-activation-link-${params.row.id}`}
              icon={<MarkEmailUnreadIcon color={getIconColor(isResendActivationLoading)} />}
              label="Resend Activation Link"
              onClick={() => handleResendActivationClick(params.row.id)}
              showInMenu
              disabled={
                !isApproved ||
                isCompanyDeleted ||
                (isResendActivationLoading && resendActivationCompanyId === params.row.id)
              }
            />,
          );
        }

        if (canReadCompany) {
          actions.push(
            <GridActionsCellItemLink
              key={`view-ledgers-${params.row.id}`}
              icon={
                <Tooltip title="View Ledgers">
                  <ReceiptLongIcon color={getIconColor(isCompanyDeleted)} />
                </Tooltip>
              }
              label="View Ledgers"
              href={`${locations.ledgerB2b()}?companyId=${params.row.id}&preset=all`}
              disabled={isCompanyDeleted}
            />,
          );
        }
        return actions;
      },
    },
    {
      field: 'id',
      headerName: 'Id',
      width: 70,
      valueFormatter: ({ value }) => value,
    },
    {
      field: 'name',
      headerName: 'Company Name',
      width: 180,
      valueFormatter: ({ value }) => value,
    },
    {
      field: 'owner',
      headerName: 'Owner',
      width: 150,
      valueFormatter: ({ value }) => value,
    },
    {
      field: 'deletedStatus',
      headerName: 'Deleted',
      width: 120,
      valueGetter: (params) => {
        const row = params.api.getRow(params.id) as any;
        return row?.isDeleted ? 'Deleted' : 'Active';
      },
      filterable: false,
    },
    {
      field: 'companyStatus',
      headerName: 'Company Status',
      width: 150,
      valueFormatter: ({ value }) => value?.status,
      filterable: false,
    },
    {
      field: 'crNumber',
      headerName: 'CR Number',
      width: 150,
      valueFormatter: ({ value }) => value,
    },
    {
      field: 'crExpDate',
      headerName: 'CR Expire Date',
      width: 150,
      valueFormatter: ({ value }) => value,
    },
    {
      field: 'phoneNumber',
      headerName: 'Phone Number',
      width: 150,
      valueFormatter: ({ value }) => value,
    },
    {
      field: 'address',
      headerName: 'Address',
      width: 150,
      valueFormatter: ({ value }) => value,
    },
    {
      field: 'companyAdmins',
      headerName: 'Company Admins',
      width: 350,
      valueFormatter: ({ value }) => JSON.stringify(value, null, 2),
      renderCell: (params: GridRenderCellParams<IAnyObject>) => {
        const value = {
          ...(params.value || {}),
        };
        for (const [key, formatFn] of Object.entries(mapDetailsPropertyToFormatFn)) {
          if (value && value.hasOwnProperty(key)) {
            value[key] = formatFn(value[key]);
          }
        }

        return <GridCellExpand value={JSON.stringify(value, null, 2) || ''} width={params.colDef.computedWidth} />;
      },
    },
    {
      field: 'companyBanks',
      headerName: 'Company Banks',
      width: 350,
      valueFormatter: ({ value }) => JSON.stringify(value, null, 2),
      renderCell: (params: GridRenderCellParams<IAnyObject>) => {
        const value = {
          ...(params.value || {}),
        };
        for (const [key, formatFn] of Object.entries(mapDetailsPropertyToFormatFn)) {
          if (value && value.hasOwnProperty(key)) {
            value[key] = formatFn(value[key]);
          }
        }

        return <GridCellExpand value={JSON.stringify(value, null, 2) || ''} width={params.colDef.computedWidth} />;
      },
    },
    {
      field: 'statusId',
      headerName: 'Company Status',
      width: 150,
      valueGetter: (params) => {
        const row = params.api.getRow(params.id) as any;
        return row?.companyStatus?.id || row?.statusId;
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      type: 'date',
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }
        return params.formattedValue;
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      type: 'date',
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }
        return params.formattedValue;
      },
    },
  ];

  return (
    <Container component="main" maxWidth="xl">
      {/* Title, create action and the status filter share one row so the grid starts higher. */}
      <Box
        sx={{
          pb: 2,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Typography variant="h4">Companies</Typography>
          {canCreateCompany && (
            <Fab color="primary" size="small" aria-label="Create Company" onClick={onAddNew}>
              <AddIcon />
            </Fab>
          )}
        </Box>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="company-status">Company Status</InputLabel>
          <Select
            labelId="company-status"
            value={companyStatusId !== null ? String(companyStatusId) : 'all'}
            label="Company Status"
            onChange={(event: SelectChangeEvent) => {
              const { value } = event.target;
              if (value === 'all') {
                setCompanyStatusId(null);
              } else {
                setCompanyStatusId(Number(value));
              }
            }}
            displayEmpty
            renderValue={(selected) => {
              if (selected === 'all') {
                return 'All';
              }
              const status = companyStatuses.find((s) => s.id === Number(selected));
              if (status) {
                return status.status.charAt(0).toUpperCase() + status.status.slice(1);
              }
              return selected;
            }}
          >
            <MenuItem value="all">
              <em>All</em>
            </MenuItem>
            {companyStatuses.map((status) => (
              <MenuItem key={status.id} value={String(status.id)}>
                {status.status.charAt(0).toUpperCase() + status.status.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Table
        ref={tableRef}
        columns={columns}
        fetchItems={fetchCompanies}
        rows={companies}
        loading={isLoading}
        rowCount={totalCompanies}
        externalFilter={externalFilters}
        initialSortModel={[{ field: 'id', sort: 'desc' }]}
        toolbarExportProps={{
          csvOptions: { fileName: `company-list-${Date.now()}` },
          excelOptions: { fileName: `company-list-${Date.now()}` },
        }}
      />

      {/* Unified Action Confirmation Dialog */}
      <Dialog open={actionDialog.open} fullWidth>
        <DialogTitle>
          {actionDialog.type === 'delete' && `Are you sure you want to delete company ${actionDialog.company?.name}?`}
          {actionDialog.type === 'approve' && 'Are you sure you want to approve this company?'}
          {actionDialog.type === 'suspend' && 'Are you sure you want to suspend this company?'}
          {actionDialog.type === 'inactive' && 'Are you sure you want to set this company to inactive?'}
          {actionDialog.type === 'restore' && 'Are you sure you want to restore this company?'}
        </DialogTitle>
        <DialogActions>
          <LoadingButton
            type="button"
            color="primary"
            variant="contained"
            endIcon={getActionIcon(actionDialog.type)}
            loadingPosition="end"
            loading={getActionLoading(actionDialog.type)}
            onClick={confirmAction}
            disabled={!actionDialog.type || !actionDialog.companyId || getActionLoading(actionDialog.type)}
          >
            Yes
          </LoadingButton>
          <LoadingButton
            color="secondary"
            variant="contained"
            onClick={closeActionDialog}
            disabled={getActionLoading(actionDialog.type)}
          >
            No
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
